import { useTranslation } from 'next-i18next'
import React, { useMemo, useRef } from 'react'

import type { Language, SupportedMarketKey } from '@nxt-insurance/constants'

import { useAnalytics } from '@/context/AnalyticsContext'
import { ContractDocuments } from '@/shared/components/flows/ContractDocuments'
import { ImmediateCoverageConsent } from '@/shared/components/flows/ImmediateCoverageConsent'
import { StepPayment } from '@/shared/components/flows/StepPayment'
import { SuccessStep } from '@/shared/components/flows/SuccessStep'
import type { PremiumInterval, PriceData } from '@flows-ui/services/QuoteDataContext.types'
import { QuoteDataProvider, StepContentRenderer, StepQuote } from '@flows-ui/stepRenderer'
import type { QuoteDataContextValue, StepQuoteProps } from '@flows-ui/stepRenderer'

import type { FlowStepPageConfig, FlowStepSubmit } from './types'
import type { useFlowSummary } from './useFlowSummary'

export const useFlowComponentMap = ({
  config,
  isAirpodsCampaign,
  language,
  market,
  onSubmit,
  permanentDiscountLost,
  premiumInterval,
  priceData,
  quoteDataValue,
  summary,
}: {
  config: FlowStepPageConfig
  isAirpodsCampaign: boolean
  language: Language
  market: SupportedMarketKey
  onSubmit: FlowStepSubmit
  permanentDiscountLost: boolean
  premiumInterval: PremiumInterval
  priceData: PriceData | undefined
  quoteDataValue: QuoteDataContextValue
  summary: ReturnType<typeof useFlowSummary>
}) => {
  const { t } = useTranslation()
  const { track } = useAnalytics()

  const quoteDataValueRef = useRef(quoteDataValue)
  quoteDataValueRef.current = quoteDataValue

  const quoteRendererDepsRef = useRef({ onSubmit, language, market, track })
  quoteRendererDepsRef.current = { onSubmit, language, market, track }

  const paymentDepsRef = useRef({ priceData, premiumInterval, permanentDiscountLost })
  paymentDepsRef.current = { priceData, premiumInterval, permanentDiscountLost }

  const successDepsRef = useRef({ t, language, isAirpodsCampaign, ...summary.successData })
  successDepsRef.current = { t, language, isAirpodsCampaign, ...summary.successData }

  const componentMapRef = useRef<Record<string, React.ElementType>>({})

  const productGenerationRef = useRef(quoteDataValue.productGeneration)
  productGenerationRef.current = quoteDataValue.productGeneration

  const isAirpodsCampaignRef = useRef(isAirpodsCampaign)
  isAirpodsCampaignRef.current = isAirpodsCampaign

  const { i18nNamespace, productKey, router, extraComponents, quoteComponent, newDesign } = config

  const componentMap = useMemo<Record<string, React.ElementType>>(() => {
    const QuoteComponent = quoteComponent ?? StepQuote

    const QuoteStep = (stepProps: StepQuoteProps) => {
      const deps = quoteRendererDepsRef.current
      return (
        <QuoteDataProvider value={quoteDataValueRef.current}>
          <QuoteComponent
            {...stepProps}
            submitHandler={() => deps.onSubmit()}
            stepContentRenderer={(rendererProps) => (
              <StepContentRenderer
                {...rendererProps}
                servicesResponses={{}}
                locale={`${deps.language}-${deps.market}`}
                track={deps.track}
                componentMap={componentMapRef.current}
              />
            )}
            componentMap={componentMapRef.current}
          />
        </QuoteDataProvider>
      )
    }

    const FlowStepPayment = (stepProps: {
      submitHandler: () => void | Promise<void>
      flowName: string
    }) => {
      const deps = paymentDepsRef.current
      return (
        <StepPayment
          submitHandler={stepProps.submitHandler}
          flowName={stepProps.flowName}
          productKey={productKey}
          priceData={deps.priceData}
          premiumInterval={deps.premiumInterval}
          permanentDiscountLost={deps.permanentDiscountLost}
        />
      )
    }

    const FlowSuccess = () => {
      const deps = successDepsRef.current
      return (
        <SuccessStep
          insuranceName={deps.t(`${i18nNamespace}:headerLabel`)}
          language={deps.language}
          planTitle={deps.selectedPlanTitle}
          formattedPremium={deps.formattedPremium}
          premiumLabel={deps.priceLabel}
          formattedStartDate={deps.formattedStartDate}
          isAirpodsCampaign={deps.isAirpodsCampaign}
        />
      )
    }

    return {
      QuoteStep,
      StepPayment: FlowStepPayment,
      ImmediateCoverageConsent: () => (
        <ImmediateCoverageConsent flowRouter={router} newDesign={newDesign} />
      ),
      ContractDocuments: () => (
        <ContractDocuments showAirpodsTerms={isAirpodsCampaignRef.current} />
      ),
      FlowSuccess,
      ...extraComponents?.({
        getProductGeneration: () => productGenerationRef.current,
        isAirpodsCampaign: () => isAirpodsCampaignRef.current,
      }),
    }
  }, [extraComponents, i18nNamespace, newDesign, productKey, quoteComponent, router])
  componentMapRef.current = componentMap

  return componentMap
}
